// DataWash API client — new endpoints for the ABC2 Document Intelligence
// Engine rebuild (whitewash / reconstruction / deficiency / comparison /
// transparency-on-send). All endpoints follow the API contract in
// PLAN.md. NO MOCKS — every function hits a real backend route. If the
// backend has not implemented a route yet, the caller gets a 404 and the
// UI shows a graceful "Feature pending" message (see components).

import apiClient from './client';

// ---- Types (from PLAN.md API contract) ----

export type CaseType = 'deposit_review' | 'comparison' | 'deficiency';

export interface WhitewashReportItem {
  category: string;
  tag: string;
  original_value_masked: string;
  count: number;
}

export interface WhitewashDocResult {
  document_id: string;
  tag_map_id: string;
  sanitized_text: string;
  report: {
    items_stripped: WhitewashReportItem[];
    zero_pii_confirmed: boolean;
    sanitized_hash: string;
  };
}

export interface WhitewashJobStatus {
  status: 'pending' | 'running' | 'complete' | 'error';
  progress: { completed: number; total: number };
  results: WhitewashDocResult[];
  error?: string;
}

export interface ReconstructResult {
  reconstructed_text: string;
  unknown_tags: string[];
  warnings: string[];
}

export interface DeficiencyItem {
  description: string;
  source_location: string;
  severity: string;
}

export interface DeficiencyDocResult {
  document_id: string;
  deficiencies: DeficiencyItem[];
}

export interface DeficiencyJobStatus {
  status: 'pending' | 'running' | 'complete' | 'error';
  results: { deficiencies: DeficiencyDocResult[] } | DeficiencyDocResult[];
  error?: string;
}

export interface CompareDifference {
  type: 'added' | 'removed' | 'changed';
  page: number;
  description: string;
  source_link: string;
}

export interface CompareJobStatus {
  status: 'pending' | 'running' | 'complete' | 'error';
  results: {
    differences: CompareDifference[];
    summary: string;
    original_filename?: string;
    revision_filenames?: string[];
  };
  error?: string;
}

export interface PreviewSendResult {
  sanitized_payload: string;
  zero_pii_confirmed: boolean;
  payload_hash: string;
}

export interface ConfirmSendResult {
  response_text: string;
  response_with_tags: string;
  reconstructed_text: string;
}

// ---- Whitewash ----

export async function startWhitewash(
  caseId: string,
  documentIds: string[],
): Promise<{ job_id: string; status: string }> {
  const { data } = await apiClient.post('/tools/whitewash', {
    case_id: caseId,
    document_ids: documentIds,
  });
  return data;
}

export async function getWhitewashJob(
  jobId: string,
): Promise<WhitewashJobStatus> {
  const { data } = await apiClient.get(`/tools/whitewash/${jobId}`);
  return data;
}

export async function getWhitewashReport(
  documentId: string,
): Promise<WhitewashDocResult['report'] & { report_markdown?: string | null }> {
  const { data } = await apiClient.get(`/tools/whitewash/report/${documentId}`);
  return data;
}

// ---- PII Audit Report (agent pipeline) ----

// Generate a polished PII audit report via the agent pipeline:
// stripped text -> Claude (agentic-bridge) -> reconstruct -> cached .docx.
// The bridge call can take 30-90s; the caller should show a loading state.
export async function generatePiiReport(
  documentId: string,
): Promise<{
  document_id: string;
  case_id: string;
  report_markdown: string;
  items_stripped: Record<string, number> | WhitewashReportItem[];
  zero_pii_confirmed: boolean;
  sanitized_hash: string;
  unknown_tags: string[];
  warnings: string[];
  created_at: string;
}> {
  const { data } = await apiClient.post(`/tools/whitewash/report/${documentId}/generate`);
  return data;
}

// Download the cached PII audit report as a template-styled .docx (blob).
// Renders from the persisted reconstructed markdown; does NOT re-call Claude.
export async function downloadPiiReportDocx(documentId: string): Promise<Blob> {
  const resp = await apiClient.get(`/tools/whitewash/report/${documentId}/docx`, {
    responseType: 'blob',
  });
  return resp.data as Blob;
}

// ---- Reconstruction ----

export async function reconstruct(
  caseId: string,
  textWithTags: string,
): Promise<ReconstructResult> {
  const { data } = await apiClient.post('/tools/reconstruct', {
    case_id: caseId,
    text: textWithTags,
  });
  return data;
}

export interface ReconstructFileResult {
  reconstructed: boolean;
  reason?: 'no_tags' | 'no_text' | string;
  reconstructed_text?: string;
  unknown_tags?: string[];
  warnings?: string[];
  tags_used?: number;
  original_filename?: string;
}

// Server-side detect + reconstruct for a dropped file. The browser can't
// read PII tags out of a FlateDecode-compressed PDF, so detection must
// happen after real text extraction on the backend. When `reconstructed`
// is false the caller should upload the file as a regular case document.
export async function reconstructFile(
  caseId: string,
  file: File,
): Promise<ReconstructFileResult> {
  const form = new FormData();
  form.append('case_id', caseId);
  form.append('file', file);
  const { data } = await apiClient.post('/tools/reconstruct-file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ---- Deficiency analysis ----

export async function startDeficiency(
  caseId: string,
  documentIds: string[],
  useLlm: boolean = false,
): Promise<{ job_id: string; status: string }> {
  const { data } = await apiClient.post('/tools/deficiency', {
    case_id: caseId,
    document_ids: documentIds,
    use_llm: useLlm,
  });
  return data;
}

export async function getDeficiencyJob(
  jobId: string,
): Promise<DeficiencyJobStatus> {
  const { data } = await apiClient.get(`/tools/deficiency/${jobId}`);
  return data;
}

// Fetch the latest complete deficiency job results for a case (auto-scanned
// on document ingestion). Returns {job_id, status, results}.
export async function getLatestDeficiency(
  caseId: string,
): Promise<{ job_id: string | null; status: string; results: { deficiencies: DeficiencyDocResult[] } | DeficiencyDocResult[] }> {
  const { data } = await apiClient.get(`/tools/deficiency/case/${caseId}`);
  return data;
}

// Export the deficiency list for a job as PDF (blob) or plain text (UC-1.7).
export async function exportDeficiency(
  jobId: string,
  format: 'pdf' | 'text',
): Promise<Blob> {
  const resp = await apiClient.get(`/tools/deficiency/${jobId}/export`, {
    params: { format },
    responseType: 'blob',
  });
  return resp.data;
}

/** Download the deficiency list as a Word (.docx) document. Mirrors the
 * `downloadReviewRecordDocx` blob-download pattern. Returns the Blob. */
export async function downloadDeficiencyDocx(
  jobId: string,
  caseNumber?: string,
): Promise<Blob> {
  const resp = await apiClient.get(`/tools/deficiency/${jobId}/export`, {
    params: { format: 'docx' },
    responseType: 'blob',
  });
  const blob = resp.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const base = (caseNumber || jobId).replace(/^DR-/, '');
  a.download = `deficiency-${base}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return blob;
}

// ---- Document comparison ----

export async function startCompare(
  caseId: string,
  originalDocId: string,
  revisionDocIds: string[],
  useLlm = false,
): Promise<{ job_id: string; status: string }> {
  const { data } = await apiClient.post('/tools/compare', {
    case_id: caseId,
    original_doc_id: originalDocId,
    revision_doc_ids: revisionDocIds,
    use_llm: useLlm,
  });
  return data;
}

export async function getCompareJob(jobId: string): Promise<CompareJobStatus> {
  const { data } = await apiClient.get(`/tools/compare/${jobId}`);
  return data;
}

// Export the comparison diff for a job as PDF (blob), plain text, or Word
// (.docx) (UC-2.6).
export async function exportCompare(
  jobId: string,
  format: 'pdf' | 'text' | 'docx',
): Promise<Blob> {
  const resp = await apiClient.get(`/tools/compare/${jobId}/export`, {
    params: { format },
    responseType: 'blob',
  });
  return resp.data;
}

// ---- Transparency / pre-send ----

export async function previewSend(
  caseId: string,
  documentIds: string[],
  prompt: string,
): Promise<PreviewSendResult> {
  const { data } = await apiClient.post('/tools/preview-send', {
    case_id: caseId,
    document_ids: documentIds,
    prompt,
  });
  return data;
}

export async function confirmSend(
  caseId: string,
  sanitizedPayload: string,
  payloadHash: string,
  prompt: string,
): Promise<ConfirmSendResult> {
  const { data } = await apiClient.post('/tools/confirm-send', {
    case_id: caseId,
    sanitized_payload: sanitizedPayload,
    payload_hash: payloadHash,
    prompt,
  });
  return data;
}

// ---- Polling helper ----

// Polls a job status getter every `intervalMs` until the status is
// 'complete' or 'error' (or the abort signal fires). Returns the final
// status object. Generic over the job-status shape.
export function pollJob<T extends { status: string }>(
  getStatus: () => Promise<T>,
  intervalMs = 2000,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tick = () => {
      getStatus()
        .then((s) => {
          if (s.status === 'complete') resolve(s);
          else if (s.status === 'error') reject(s);
          else {
            if (signal?.aborted) {
              reject(new DOMException('Aborted', 'AbortError'));
              return;
            }
            setTimeout(tick, intervalMs);
          }
        })
        .catch(reject);
    };
    tick();
  });
}